import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Box, Stack } from '@mui/material';
import SelectGroup from '../../../components/casino/SelectGroup';
import Grid from '@mui/material/Grid2';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import CurrencySelect from '../../../components/shared/CurrencySelect';
import CusDatePicker from '../../../components/material-ui/CusDatePicker';
import { useTranslation } from 'react-i18next';
import CusInput from '../../../components/material-ui/CusInput';
import useToast from '../../../hooks/useToast';
import CusButton from '../../../components/material-ui/CusButton';
import CusRadioGroup from '../../../components/material-ui/radio_group/CusRadioGroup';
import { useAppSelector } from '../../../store';
import { ENUM_DATE_MEDE } from '../../../api/report';
import { getOnlyData } from '../../../utils';
import dayjs from 'dayjs';
import { API_GROUP } from '../../../api/user';
import DatePreset from '../../../components/material-ui/DatePreset';

export default function ReportPlayerFilter({ onFindClick }) {
  const { t } = useTranslation();
  const { errorToast } = useToast();
  const mainGroup = useAppSelector((state) => state.user.group.main);
  const mainGroupId = mainGroup.groupId;
  const groupHash = useAppSelector((state) => state.user.group.hash);
  const payload = useAppSelector((state) => state.reports.gamers.payload);

  const prevWeek = getOnlyData(dayjs().subtract(7, 'day'));
  const today = getOnlyData(dayjs());

  const [refcodeOptions, setRefcodeOptions] = useState([]);
  const [filterInfo, setFilterInfo] = useState({
    groupId: mainGroupId,
    refcodeId: 0,
    currency: mainGroup.currency,
    minDepositCount: 'not',
    dateMode: ENUM_DATE_MEDE.register,
    dateFrom: prevWeek,
    dateTo: today,
    userId: '',
    userLogin: '',
    ...payload
  });

  const countDepOptions = [{ label: t('Not specified'), value: 'not' }];
  new Array(11).fill(1).forEach((_, indx) => {
    countDepOptions.push({ label: indx, value: indx });
  });

  const dateFrom = filterInfo.dateFrom;
  const dateTo = filterInfo.dateTo;
  const groupId = filterInfo.groupId;
  const currentGroup = groupHash[groupId];

  const gridSize = { md: 4, sm: 6, xs: 12 };
  const gridSize2 = { sm: 3, sx: 6 };
  const gridSize3 = { sm: 6, xs: 12 };

  useLayoutEffect(() => {
    if (groupId) return;
    setFilterInfo((prev) => ({
      ...prev,
      currency: mainGroup?.currency || '',
      groupId: mainGroupId
    }));
  }, [groupId, mainGroupId]);

  useEffect(() => {
    if (!groupId) return;

    API_GROUP.refCode
      .refCodes({ groupId })
      .then((e) => {
        const data = e.data.data;
        setRefcodeOptions(data.map((el) => ({ label: el[1], value: el[0] })));
      })
      .catch(console.error);
  }, [groupId]);

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <SelectGroup
          title="Group"
          hideTooltip
          selectGroupId={groupId}
          setSelectGroupId={(val) => {
            setFilterInfo((prev) => ({ ...prev, groupId: val, userId: '' }));
          }}
        />
      </Grid>
      <Grid size={gridSize}>
        <CusLabelSelect
          label={'Ref code'}
          value={filterInfo.refcodeId}
          onChange={(val) => setFilterInfo((prev) => ({ ...prev, refcodeId: val }))}
          options={[{ label: t('All'), value: 0 }, ...refcodeOptions]}
          toolTipText="You can only select a referral code from a game group."
          disabled={currentGroup && !currentGroup.game}
        />
      </Grid>
      <Grid size={gridSize}>
        <CurrencySelect
          value={filterInfo.currency}
          setValue={(val) => setFilterInfo((prev) => ({ ...prev, currency: val }))}
        />
      </Grid>
      <Grid size={gridSize}>
        <CusLabelSelect
          label={'Deposits made at least'}
          value={filterInfo.minDepositCount}
          onChange={(val) => setFilterInfo((prev) => ({ ...prev, minDepositCount: val }))}
          options={countDepOptions}
        />
      </Grid>
      <Grid size={gridSize3} mt={1}>
        <CusRadioGroup
          value={filterInfo.dateMode}
          setValue={(val) => setFilterInfo((prev) => ({ ...prev, dateMode: val }))}
          label={'Date selection'}
          options={[
            { label: 'Registration', value: 'register' },
            { label: 'First deposit', value: 'firstDep' }
          ]}
        />
      </Grid>
      <Grid size={gridSize2}>
        <Box sx={{ backgroundColor: (theme) => theme.palette.grey[100] }} p={1}>
          <CusInput
            label={'Login'}
            value={filterInfo.userLogin}
            disabled={filterInfo.userId}
            onChange={(e) =>
              setFilterInfo((prev) => ({ ...prev, userLogin: e.target.value || null }))
            }
          />
        </Box>
      </Grid>
      <Grid size={gridSize2}>
        <Box sx={{ backgroundColor: (theme) => theme.palette.grey[100] }} p={1}>
          <CusInput
            label={'User Id'}
            value={filterInfo.userId}
            disabled={filterInfo.userLogin}
            onChange={(e) => {
              const value = +e.target.value || null;
              if (!+value && value !== null) {
                errorToast('enter number');
                return;
              }
              setFilterInfo((prev) => ({ ...prev, userId: value || null }));
            }}
          />
        </Box>
      </Grid>
      <Grid container size={{ sm: 10, xs: 12 }}>
        <Grid size={{ sm: 6, xs: 12 }} mt={1}>
          <CusDatePicker
            key={'userStartDate'}
            label="Start Date"
            value={dateFrom}
            onChange={(val) => setFilterInfo((prev) => ({ ...prev, dateFrom: val }))}
            formatChange="YYYY-MM-DD"
          />
        </Grid>
        <Grid size={{ sm: 6, xs: 12 }} mt={1}>
          <CusDatePicker
            key={'userEndDate'}
            label="End Date"
            value={dateTo}
            onChange={(val) => setFilterInfo((prev) => ({ ...prev, dateTo: val }))}
            formatChange="YYYY-MM-DD"
            errorText={
              dateFrom && dateTo && new Date(dateTo).getTime() < new Date(dateFrom).getTime()
                ? t('End date must be later than start date')
                : ''
            }
          />
        </Grid>
        <Grid size={12}>
          <DatePreset
            setStartDate={(val) => setFilterInfo((prev) => ({ ...prev, dateFrom: val }))}
            setEndDate={(val) => setFilterInfo((prev) => ({ ...prev, dateTo: val }))}
            onlyDate
          />
        </Grid>
      </Grid>
      <Grid size={{ sm: 2, xs: 12 }} mt={1}>
        <Stack display={'flex'}>
          <CusButton
            text="Find"
            variant="contained"
            sx={{ px: 5 }}
            onClick={() => onFindClick(filterInfo)}
            ml="auto"
          />
        </Stack>
      </Grid>
    </Grid>
  );
}
